/**
 * Directory and Locations — **one project's people, companies and places**,
 * drawn from the design's `Led AI - Project Details.dc.html` artboard (project
 * `806e74fa`, design system `f7c932`).
 *
 * Both pages exist only with a project in scope: a directory is per project,
 * so under "All projects" the sidebar does not offer them, and a bookmark that
 * lands here without a project gets the artboard's project list instead.
 *
 * ## Three sources, one page
 *
 * - **Procore** and **Autodesk** projects read the provider's own directory and
 *   location tree, live, on the reader's own login (`/api/project-details`).
 *   Nothing is editable here: the provider is the system of record, and the
 *   source line links to it.
 * - **Led AI** projects have no provider, so they read the files an admin
 *   uploaded. Until one is, the page is the upload card. A file is checked by
 *   the server and REVIEWED before it is saved — "Nothing is saved until you
 *   review what's inside" is the design's promise, and `?validate=1` is what
 *   keeps it: the first POST parses and returns, the second one saves.
 *
 * ## Colours are the artboard's, not `C`
 *
 * As on the Team page, which shares its paper and ink: see `People.tsx`.
 */

import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  ApiError,
  fetchProjectDirectory,
  fetchProjectLocations,
  saveProjectFile,
  validateProjectFile,
  ProjectFileError,
  type ConsoleSite,
  type ProjectDetailsDirectory,
  type ProjectDetailsDirectoryPage,
  type ProjectDetailsError,
  type ProjectDetailsFreshness,
  type ProjectDetailsLocation,
  type ProjectDetailsLocationNode,
  type ProjectDetailsLocationsPage,
  type ProjectDetailsPerson,
  type ProjectDetailsSummary,
  type ProjectFileValidation,
} from "../api";
import type { Resource } from "../useResource";
import { resourceQuery } from "../resourceCache";
import { DS, FONT_MONO } from "../tokens";
import { PROCORE_MARK_URL } from "../assets/procoreMark";
import { DsIcon } from "./TeamProcore";
import { useMediaQuery } from "../shell/useMediaQuery";
import { openIntegrations } from "../components/integrationsEvents";
import {
  ASK_COMPOSER_CLEARANCE,
  FailurePanel,
  LoadingPanel,
  StaleBanner,
  type ConsoleViewProps,
} from "../components/chrome";
import {
  BRANCH_FILL,
  NONE_FILL,
  OTHER_FILL,
  buildDirectory,
  buildLocationTree,
  crewSegments,
  initials,
  levelLabel,
  matchesPerson,
  pathTo,
  rankCompanies,
  visibleRows,
  type CompanyGroup,
  type CrewSegment,
} from "../../../../backend/src/shared/projectDetailsModel";

export type ProjectDetailsTab = "directory" | "locations";

export interface ProjectDetailsProps extends ConsoleViewProps {
  tab: ProjectDetailsTab;
  /** The project in scope, or null under "All projects". */
  site: ConsoleSite | null;
  sites: ConsoleSite[];
  /** The summary — owned by the shell, which draws the rail's counts from
   *  it. Costs no provider call; the pages below read their own data. */
  details: Resource<ProjectDetailsSummary>;
  onReload: () => void;
}

// ---------------------------------------------------------------------------
// The artboard's own values, under the names the artboard gives them. Each is
// a design-system token: `tokens.ts` is the only file that may spell a colour
// (`tokens.test.ts` walks this directory to enforce it), so the values live
// there and this page keeps only the artboard's vocabulary for them.
// ---------------------------------------------------------------------------

const INK3 = DS.inkMuted;
const WARN_INK = DS.warnInk;
const SYNC_INK = DS.syncInk;
const CHIP = DS.chip;
/** The neutral ground of anything at rest — a segment track, a source mark
 *  with no provider, the step disc not yet reached, the disabled gold
 *  button. The same value the people picker's "+N" pip sits on. */
const SEG_TRACK = DS.pickerOverflow;
const HEAD_BG = DS.tableHead;
const SOFT = DS.soft;
const HOVER_ROW = DS.treeHover;
const GOLD_BTN = `linear-gradient(180deg,${DS.goldLight},${DS.goldDeep})`;
const GOLD_SHADOW = "0 10px 24px rgba(247,197,43,.3)";
const ROW_LINE = "1px solid rgba(10,10,10,.06)";
const CARD_LINE = "1px solid rgba(10,10,10,.08)";
const FIELD_LINE = "1px solid rgba(10,10,10,.13)";

/** People drawn per page of the table; the footer offers the next page. */
const PAGE = 40;

const PANE: React.CSSProperties = {
  flex: 1,
  minHeight: 0,
  overflow: "auto",
  padding: `0 0 ${ASK_COMPOSER_CLEARANCE}px`,
  paddingBottom: `var(--ask-composer-clearance, ${ASK_COMPOSER_CLEARANCE}px)`,
};

const PAGE_BOX: React.CSSProperties = {
  width: "100%",
  maxWidth: 1080,
  margin: "0 auto",
  boxSizing: "border-box",
  padding: "36px 32px 0",
};

const EYEBROW: React.CSSProperties = {
  fontFamily: FONT_MONO,
  fontSize: 10,
  fontWeight: 700,
  letterSpacing: "1.5px",
  color: DS.meta,
  textTransform: "uppercase",
};

const MONO_META: React.CSSProperties = { fontFamily: FONT_MONO, fontSize: 10, fontWeight: 600, letterSpacing: "1px", color: DS.meta };

const TABLE: React.CSSProperties = { border: CARD_LINE, borderRadius: 14, overflow: "hidden", background: DS.surface };

const TABLE_HEAD: React.CSSProperties = {
  fontFamily: FONT_MONO,
  display: "grid",
  gap: 12,
  padding: "9px 16px",
  background: HEAD_BG,
  borderBottom: CARD_LINE,
  fontSize: 9.5,
  fontWeight: 700,
  letterSpacing: "1.3px",
  color: DS.meta,
};

const ELLIPSIS: React.CSSProperties = { whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" };

const LINK_BUTTON: React.CSSProperties = {
  border: 0,
  padding: 0,
  background: "transparent",
  font: "inherit",
  fontSize: 13,
  fontWeight: 600,
  color: DS.ink,
  cursor: "pointer",
  whiteSpace: "nowrap",
};

const providerName = (source: ProjectDetailsSummary["source"] | undefined) =>
  source === "procore" ? "Procore" : source === "autodesk" ? "Autodesk" : "";

const plural = (count: number, one: string, many = one + "s") => `${count.toLocaleString()} ${count === 1 ? one : many}`;

// ---------------------------------------------------------------------------
// Small parts
// ---------------------------------------------------------------------------

/**
 * Autodesk's mark: the 2021 folded-ribbon A — the same path the phone's
 * create flow (`providerMarks.tsx`) and the sign-in frame draw — in white on
 * Autodesk's black app tile. It used to be a hand-drawn letter A, which read
 * as a generic glyph rather than as Autodesk. Black, like Procore's own tile
 * beside it, so the two marks read as a pair at 12–14px.
 */
function AutodeskMark({ size }: { size: number }) {
  return (
    <span
      role="img"
      aria-label="Autodesk"
      style={{ width: size, height: size, flexShrink: 0, display: "grid", placeItems: "center", borderRadius: size > 14 ? 4 : 3, background: DS.ink }}
    >
      <svg width={Math.round(size * 0.7)} height={Math.round(size * 0.7)} viewBox="0 0 24 24" aria-hidden style={{ display: "block" }}>
        <path
          d="m.129 20.202 14.7-9.136h7.625c.235 0 .445.188.445.445 0 .21-.092.305-.21.375l-7.222 4.323c-.47.283-.633.845-.633 1.265l-.008 2.725H24V4.362a.561.561 0 0 0-.585-.562h-8.752L0 12.893V20.2h.129z"
          fill={DS.surface}
        />
      </svg>
    </span>
  );
}

export function SourceMark({ source, size = 14 }: { source?: "procore" | "autodesk" | "files" | undefined; size?: number }) {
  if (source === "procore") {
    return <img src={PROCORE_MARK_URL} alt="Procore" width={size} height={size} style={{ display: "block", flexShrink: 0, borderRadius: size > 14 ? 4 : 3 }} />;
  }
  if (source === "autodesk") return <AutodeskMark size={size} />;
  return <span aria-hidden style={{ width: size, height: size, borderRadius: 4, background: SEG_TRACK, display: "block", flexShrink: 0 }} />;
}

function UploadGlyph({ color = DS.surface }: { color?: string }) {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" aria-hidden style={{ display: "block", flexShrink: 0 }}>
      <path d="M12 16V4m0 0-5 5m5-5 5 5M4 17v2a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1v-2" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

/** The People / Companies switch, and the review modal's filter pills. */
function Segmented<T extends string>({
  options,
  value,
  onChange,
  track = SEG_TRACK,
  testId,
}: {
  options: Array<{ id: T; label: string; count?: string; warn?: boolean; mark?: string }>;
  value: T;
  onChange: (id: T) => void;
  track?: string;
  testId?: string;
}) {
  return (
    <div role="tablist" data-testid={testId} style={{ display: "flex", gap: 4, padding: 3, borderRadius: 999, background: track }}>
      {options.map((option) => {
        const on = option.id === value;
        return (
          <button
            key={option.id}
            type="button"
            role="tab"
            aria-selected={on}
            className="kb"
            onClick={() => onChange(option.id)}
            style={{
              height: 28,
              padding: "0 12px",
              border: 0,
              borderRadius: 14,
              background: on ? DS.surface : "transparent",
              boxShadow: on ? "0 1px 3px rgba(10,10,10,.12)" : "none",
              display: "flex",
              alignItems: "center",
              gap: 6,
              font: "inherit",
              fontSize: 12.5,
              fontWeight: 700,
              color: on ? DS.ink : DS.body2,
              cursor: "pointer",
              whiteSpace: "nowrap",
            }}
          >
            {option.warn && <span aria-hidden style={{ width: 6, height: 6, borderRadius: 3, background: DS.warnDot, display: "block" }} />}
            {(option.mark === "procore" || option.mark === "autodesk") && <SourceMark source={option.mark} size={13} />}
            {option.label}
            {option.count !== undefined && <span style={{ fontFamily: FONT_MONO, fontSize: 10, fontWeight: 600, color: DS.meta }}>{option.count}</span>}
          </button>
        );
      })}
    </div>
  );
}

function SearchField({
  value,
  onChange,
  placeholder,
  height = 42,
  testId,
  busy = false,
}: {
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
  height?: number;
  testId?: string;
  /** A search in flight: the glyph becomes a spinner, the field stays usable. */
  busy?: boolean;
}) {
  return (
    <label style={{ flex: 1, minWidth: 0, display: "flex", alignItems: "center", gap: 9, height, padding: "0 14px", borderRadius: 12, border: FIELD_LINE, background: DS.surface, boxSizing: "border-box" }}>
      {busy ? <Spinner size={15} /> : <DsIcon name="search" size={15} color={DS.meta} />}
      <input
        data-testid={testId}
        value={value}
        // `onInput` as well, as elsewhere in the console: it is the native
        // event, and the one a test (and any non-React caller) can fire.
        onChange={(event) => onChange(event.target.value)}
        onInput={(event) => onChange((event.target as HTMLInputElement).value)}
        placeholder={placeholder}
        aria-label={placeholder}
        style={{ flex: 1, width: 0, minWidth: 0, border: "none", outline: "none", background: "transparent", font: "inherit", fontSize: 14, color: DS.ink }}
      />
      {value && (
        <button
          type="button"
          aria-label="Clear search"
          onClick={() => onChange("")}
          style={{ width: 18, height: 18, border: 0, padding: 0, borderRadius: 9, background: DS.body2, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer" }}
        >
          <DsIcon name="close" size={10} color={DS.surface} />
        </button>
      )}
    </label>
  );
}

// ---------------------------------------------------------------------------
// The crew bar — with hover INTENT
// ---------------------------------------------------------------------------

/**
 * How long the pointer must rest on a segment before the list filters, and
 * how long it may leave before the filter lets go. Sweeping across the bar
 * on the way somewhere else must not reshuffle the list under the reader;
 * moving from one segment to its neighbour while already filtering is a
 * deliberate act, so it commits sooner.
 */
export const HOVER_COMMIT_MS = 280;
export const HOVER_SWITCH_MS = 150;
export const HOVER_RELEASE_MS = 220;

export function useHoverIntent(onCommit: (id: string | null) => void, committed: string | null) {
  const [pending, setPending] = useState<string | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const commit = useRef(onCommit);
  commit.current = onCommit;
  useEffect(() => () => clearTimeout(timer.current), []);
  return {
    pending,
    enter(id: string) {
      clearTimeout(timer.current);
      if (id === committed) {
        setPending(null);
        return;
      }
      setPending(id);
      timer.current = setTimeout(() => {
        setPending(null);
        commit.current(id);
      }, committed ? HOVER_SWITCH_MS : HOVER_COMMIT_MS);
    },
    leave() {
      clearTimeout(timer.current);
      setPending(null);
      if (committed) timer.current = setTimeout(() => commit.current(null), HOVER_RELEASE_MS);
    },
  };
}

/** The crew bar and its legend. Resting on a segment first eases the others
 *  back a little (the preview), then — once the intent holds — filters. */
function CrewBar({ crew, hover, onHover }: { crew: CrewSegment[]; hover: string | null; onHover: (id: string | null) => void }) {
  const intent = useHoverIntent(onHover, hover);
  if (!crew.length) return null;
  const opacity = (id: string) => (hover ? (hover === id ? 1 : 0.28) : intent.pending ? (intent.pending === id ? 1 : 0.62) : 1);
  return (
    <div onMouseLeave={intent.leave}>
      <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between" }}>
        <span style={{ ...EYEBROW, color: DS.ink }}>Crew by company</span>
        <span style={{ fontSize: 12.5, color: DS.meta }}>{hover ? "Move away to show everyone" : "Hover a segment"}</span>
      </div>
      <div className="pd-grow" data-testid="crew-bar" style={{ display: "flex", gap: 3, height: 16, marginTop: 10, borderRadius: 8, overflow: "hidden" }}>
        {crew.map((segment) => (
          <span
            key={segment.id}
            title={`${segment.name} · ${segment.count}`}
            data-crew={segment.id}
            onMouseEnter={() => intent.enter(segment.id)}
            style={{ flex: `${segment.count} 1 0`, background: segment.fill, opacity: opacity(segment.id), transition: "opacity .28s ease", display: "block" }}
          />
        ))}
      </div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: "6px 14px", marginTop: 11 }}>
        {crew.map((segment) => (
          <span
            key={segment.id}
            onMouseEnter={() => intent.enter(segment.id)}
            style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12.5, fontWeight: 600, color: INK3, opacity: opacity(segment.id), transition: "opacity .28s ease" }}
          >
            <span aria-hidden style={{ width: 9, height: 9, borderRadius: 3, background: segment.fill, display: "block" }} />
            {segment.short}
            <span style={{ fontFamily: FONT_MONO, fontSize: 10, color: DS.meta }}>{segment.count}</span>
          </span>
        ))}
      </div>
    </div>
  );
}

type PersonRow = ProjectDetailsPerson & { groupId: string | null };

/** People rows. `footer` is the paging control — local or remote. */
function PeopleTable({
  companies,
  people,
  footer,
  sourceTag,
  empty = "No one matches.",
  dim = false,
}: {
  companies: CompanyGroup[];
  people: PersonRow[];
  footer?: React.ReactNode;
  sourceTag?: string;
  empty?: string;
  dim?: boolean;
}) {
  const byId = new Map(companies.map((company) => [company.id, company]));
  const columns = sourceTag ? "minmax(0,1.3fr) minmax(0,1fr) minmax(0,1.2fr) 110px" : "minmax(0,1.3fr) minmax(0,1fr) minmax(0,1.2fr)";
  return (
    <div style={{ ...TABLE, opacity: dim ? 0.6 : 1, transition: "opacity .15s ease" }} data-testid="people-table">
      <div style={{ ...TABLE_HEAD, gridTemplateColumns: columns }}>
        <span>NAME</span>
        <span>ROLE</span>
        <span>COMPANY</span>
        {sourceTag && <span>SOURCE</span>}
      </div>
      {people.map((person, index) => {
        const company = person.groupId ? byId.get(person.groupId) : undefined;
        return (
          <div key={person.id} className="pd-row" data-testid="person-row" style={{ display: "grid", gridTemplateColumns: columns, gap: 12, alignItems: "center", padding: "10px 16px", borderTop: index ? ROW_LINE : "none" }}>
            <span style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0 }}>
              <span aria-hidden style={{ flex: "none", width: 28, height: 28, borderRadius: 14, background: company?.top ? company.fill : SEG_TRACK, color: company?.top ? company.fg : company ? DS.body2 : DS.meta, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 10.5, fontWeight: 800 }}>
                {initials(person.name)}
              </span>
              <span title={person.email ?? person.name} style={{ fontSize: 14, fontWeight: 700, letterSpacing: "-.2px", ...ELLIPSIS }}>{person.name}</span>
            </span>
            <span style={{ fontSize: 13.5, color: person.role ? INK3 : WARN_INK, ...ELLIPSIS }}>{person.role || "No role"}</span>
            <span style={{ display: "flex", alignItems: "center", gap: 7, minWidth: 0 }}>
              <span aria-hidden style={{ flex: "none", width: 8, height: 8, borderRadius: 2, background: company ? company.fill : NONE_FILL, display: "block" }} />
              <span style={{ fontSize: 13.5, fontWeight: 600, color: company ? DS.ink : WARN_INK, ...ELLIPSIS }}>{company?.name ?? person.company ?? "No company"}</span>
            </span>
            {sourceTag && <span style={{ fontFamily: FONT_MONO, fontSize: 9.5, fontWeight: 600, letterSpacing: ".8px", color: DS.metaQuiet, whiteSpace: "nowrap" }}>{sourceTag}</span>}
          </div>
        );
      })}
      {people.length === 0 && <div style={{ padding: "28px 16px", textAlign: "center", fontSize: 13.5, color: DS.meta }}>{empty}</div>}
      {footer}
    </div>
  );
}

function MoreButton({ shown, of, onMore, busy, testId }: { shown: number; of: number; onMore: () => void; busy?: boolean; testId?: string }) {
  return (
    <button
      type="button"
      className="pd-row kb"
      data-testid={testId}
      onClick={onMore}
      disabled={busy}
      style={{ ...MONO_META, display: "flex", alignItems: "center", gap: 8, width: "100%", textAlign: "left", padding: "11px 16px", border: 0, borderTop: ROW_LINE, background: HEAD_BG, cursor: busy ? "default" : "pointer" }}
    >
      {busy && <Spinner size={12} />}
      SHOWING {shown.toLocaleString()} OF {of.toLocaleString()} · {busy ? "LOADING…" : `SHOW ${Math.min(PAGE, of - shown)} MORE`}
    </button>
  );
}

function CompaniesTable({ companies }: { companies: CompanyGroup[] }) {
  const columns = "minmax(0,1.4fr) minmax(0,1.4fr) 80px minmax(0,1fr)";
  const max = Math.max(1, ...companies.map((company) => company.count));
  return (
    <div className="pd-enter" style={TABLE} data-testid="companies-table">
      <div style={{ ...TABLE_HEAD, gridTemplateColumns: columns }}>
        <span>COMPANY</span>
        <span>ROLES</span>
        <span>PEOPLE</span>
        <span>SHARE OF CREW</span>
      </div>
      {companies.map((company, index) => (
        <div key={company.id} className="pd-row" style={{ display: "grid", gridTemplateColumns: columns, gap: 12, alignItems: "center", padding: "10px 16px", borderTop: index ? ROW_LINE : "none" }}>
          <span style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0 }}>
            <span aria-hidden style={{ flex: "none", width: 28, height: 28, borderRadius: 8, background: company.fill, color: company.fg, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 10.5, fontWeight: 800 }}>
              {initials(company.name)}
            </span>
            <span style={{ fontSize: 14, fontWeight: 700, letterSpacing: "-.2px", ...ELLIPSIS }}>{company.name}</span>
          </span>
          <span style={{ fontSize: 13.5, color: DS.body2, ...ELLIPSIS }}>{company.trade ? `${company.trade}${company.roles !== "—" ? " · " + company.roles : ""}` : company.roles}</span>
          <span style={{ fontFamily: FONT_MONO, fontSize: 12.5, fontWeight: 700 }}>{company.count}</span>
          <span style={{ height: 8, borderRadius: 4, background: CHIP, overflow: "hidden" }}>
            <span style={{ display: "block", height: "100%", width: `${Math.round((company.count / max) * 100)}%`, borderRadius: 4, background: company.fill }} />
          </span>
        </div>
      ))}
      {companies.length === 0 && <div style={{ padding: "28px 16px", textAlign: "center", fontSize: 13.5, color: DS.meta }}>No companies listed.</div>}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Reading the server — paged, searched, cached for the session
// ---------------------------------------------------------------------------

/**
 * Every page, search and subtree is a request to OUR server, which answers it
 * from one cached provider snapshot (`backend/.../projectDetails.service.ts`).
 * A short in-browser cache on top makes going back to a page, or retyping a
 * search, instant. `clearDetailsCache` after an upload replaces a file.
 */
const BROWSER_TTL_MS = 60_000;
const browserCache = new Map<string, { at: number; value: Promise<unknown> }>();
function cachedRead<T>(key: string, read: () => Promise<T>): Promise<T> {
  const hit = browserCache.get(key);
  if (hit && Date.now() - hit.at < BROWSER_TTL_MS) return hit.value as Promise<T>;
  const value = read();
  browserCache.set(key, { at: Date.now(), value });
  value.catch(() => browserCache.delete(key));
  while (browserCache.size > 200) browserCache.delete(browserCache.keys().next().value!);
  return value;
}
export function clearDetailsCache() {
  browserCache.clear();
}

export function useDebounced<T>(value: T, ms: number): T {
  const [settled, setSettled] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setSettled(value), ms);
    return () => clearTimeout(timer);
  }, [value, ms]);
  return settled;
}

/** Search waits for a pause in typing: one request per thought, not per key. */
export const SEARCH_DEBOUNCE_MS = 250;

export interface HalfMeta {
  counts: string;
  freshness: ProjectDetailsFreshness;
}

function asFailure(error: unknown): ApiError {
  return error instanceof ApiError ? error : new ApiError(0, (error as Error)?.message || "Request failed");
}

// ---------------------------------------------------------------------------
// Directory (populated) — lazy pages, server-side search
// ---------------------------------------------------------------------------

function DirectoryBody({
  siteId,
  search,
  sourceTag,
  emptyText,
  onMeta,
  reloadKey,
}: {
  siteId: string;
  search: string;
  sourceTag: string;
  emptyText: string;
  onMeta: (meta: HalfMeta) => void;
  reloadKey: number;
}) {
  const [view, setView] = useState<"people" | "companies">("people");
  const [bucket, setBucket] = useState<string | null>(null);
  const [query, setQuery] = useState("");
  const q = useDebounced(query.trim(), SEARCH_DEBOUNCE_MS);
  const [state, setState] = useState<{ key: string; first: ProjectDetailsDirectoryPage | null; people: PersonRow[]; loading: boolean; more: boolean; failure: ApiError | null }>({
    key: "",
    first: null,
    people: [],
    loading: true,
    more: false,
    failure: null,
  });
  const key = `${siteId}|${q}|${bucket ?? ""}|${reloadKey}`;
  const read = (offset: number) =>
    cachedRead(`dir|${siteId}|${resourceQuery(search, ["workspace"])}|${q}|${bucket ?? ""}|${offset}|${reloadKey}`, () =>
      fetchProjectDirectory(siteId, search, { q: q || undefined, bucket: bucket ?? undefined, offset, limit: PAGE }),
    );

  useEffect(() => {
    let live = true;
    setState((prev) => ({ ...prev, key, loading: true, failure: null }));
    read(0).then(
      (page) => live && setState({ key, first: page, people: page.people, loading: false, more: false, failure: null }),
      (error) => live && setState((prev) => ({ ...prev, key, loading: false, failure: asFailure(error) })),
    );
    return () => {
      live = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `key` covers every input
  }, [key]);

  const first = state.first;
  const companies = useMemo(() => (first ? rankCompanies(first.companies) : []), [first?.companies]);
  const crew = useMemo(() => (first ? crewSegments(companies, first.noCompany) : []), [companies, first?.noCompany]);
  const matched = first?.matched ?? 0;
  const canMore = !!first && state.key === key && state.people.length < matched;

  const loadMore = () => {
    if (!canMore || state.more || state.loading) return;
    const offset = state.people.length;
    setState((prev) => ({ ...prev, more: true }));
    read(offset).then(
      (page) => setState((prev) => (prev.key === key ? { ...prev, people: [...prev.people, ...page.people], more: false } : prev)),
      () => setState((prev) => ({ ...prev, more: false })),
    );
  };

  // The next page loads as the reader nears the end — "show more" stays as
  // the fallback for a reader who never scrolls.
  const sentinel = useRef<HTMLDivElement>(null);
  const loadMoreRef = useRef(loadMore);
  loadMoreRef.current = loadMore;
  useEffect(() => {
    const node = sentinel.current;
    if (!node || typeof IntersectionObserver === "undefined") return;
    // Rooted on the page's own scroller: the margin then means "400px before
    // the reader gets there" rather than nothing (the viewport never scrolls).
    const scroller = node.closest<HTMLElement>("[data-pd-scroll]");
    const observer = new IntersectionObserver((seen) => seen.some((entry) => entry.isIntersecting) && loadMoreRef.current(), { root: scroller, rootMargin: "400px 0px" });
    observer.observe(node);
    return () => observer.disconnect();
  }, [canMore, view]);

  useEffect(() => {
    if (!first) return;
    onMeta({
      counts: `${plural(first.total, "person", "people")} · ${plural(first.companies.length, "company", "companies")}`,
      freshness: first,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [first?.total, first?.updatedAt, first?.stale, first?.error]);

  if (!first) return state.failure ? <ProviderProblem error="fetch_failed" source="the server" onRetry={() => { clearDetailsCache(); setState((p) => ({ ...p, key: "" })); }} /> : <LoadingPanel />;
  if (first.error) return null; // the page draws the provider problem
  if (!first.total) return <ProviderEmpty text={emptyText} />;

  const hovered = bucket ? crew.find((segment) => segment.id === bucket) : undefined;
  const label =
    view === "companies"
      ? `ALL ${companies.length} COMPANIES · LARGEST FIRST`
      : hovered
        ? `${hovered.name.toUpperCase()} · ${matched}`
        : q
          ? `${matched} MATCHES`
          : `${first.total.toLocaleString()} PEOPLE · A–Z`;

  return (
    <div className="pd-enter" style={{ marginTop: 20 }}>
      <CrewBar crew={crew} hover={bucket} onHover={setBucket} />
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, marginTop: crew.length ? 26 : 0, flexWrap: "wrap" }}>
        <span style={{ ...EYEBROW, color: DS.ink }} data-testid="directory-list-label">{label}</span>
        <Segmented
          testId="directory-view"
          value={view}
          onChange={setView}
          options={[
            { id: "people", label: "People", count: String(first.total) },
            { id: "companies", label: "Companies", count: String(companies.length) },
          ]}
        />
      </div>
      {view === "companies" ? (
        <div style={{ marginTop: 10 }}>
          <CompaniesTable companies={companies} />
        </div>
      ) : (
        <>
          <div style={{ display: "flex", marginTop: 10 }}>
            <SearchField testId="directory-search" value={query} onChange={setQuery} busy={state.loading && !!state.people.length} placeholder={`Search ${plural(first.total, "person", "people")} by name, role or company`} />
          </div>
          {/* Keyed on the filter, so a new crew or search eases in rather than snapping. */}
          <div key={`${q}|${bucket ?? ""}`} className="pd-swap" style={{ marginTop: 8 }}>
            <PeopleTable
              companies={companies}
              people={state.people}
              sourceTag={sourceTag}
              dim={state.loading}
              empty={q ? `No one matches “${q}”.` : "No one here."}
              footer={
                <>
                  {canMore && <MoreButton testId="directory-more" shown={state.people.length} of={matched} busy={state.more} onMore={loadMore} />}
                  <div ref={sentinel} aria-hidden style={{ height: 1 }} />
                </>
              }
            />
          </div>
        </>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Locations (populated) — children on demand, server-side search
// ---------------------------------------------------------------------------

/** Top-level branches opened on arrival — beyond this the reader picks. */
const AUTO_OPEN_MAX = 12;
const ROOT = "";

function LocationsBody({
  siteId,
  search,
  emptyText,
  onMeta,
  reloadKey,
}: {
  siteId: string;
  search: string;
  emptyText: string;
  onMeta: (meta: HalfMeta) => void;
  reloadKey: number;
}) {
  const narrow = useMediaQuery("(max-width: 760px)");
  const [nodes, setNodes] = useState(() => new Map<string, ProjectDetailsLocationNode>());
  const [kids, setKids] = useState(() => new Map<string, string[]>());
  const [loadingIds, setLoadingIds] = useState(() => new Set<string>());
  const [first, setFirst] = useState<ProjectDetailsLocationsPage | null>(null);
  const [failure, setFailure] = useState<ApiError | null>(null);
  const [scope, setScope] = useState<string | null>(null);
  const [open, setOpen] = useState(() => new Set<string>());
  const [query, setQuery] = useState("");
  const q = useDebounced(query.trim(), SEARCH_DEBOUNCE_MS);
  const [found, setFound] = useState<{ q: string; page: ProjectDetailsLocationsPage } | null>(null);
  const [truncated, setTruncated] = useState(false);
  const [hover, setHover] = useState<string | null>(null);
  const ws = resourceQuery(search, ["workspace"]);

  const merge = (page: ProjectDetailsLocationsPage, parentKey?: string) => {
    setNodes((prev) => {
      const next = new Map(prev);
      for (const node of page.nodes) next.set(node.id, node);
      return next;
    });
    if (parentKey !== undefined) {
      setKids((prev) => new Map(prev).set(parentKey, page.nodes.map((node) => node.id)));
    }
  };

  const loadChildren = (parentKey: string) => {
    if (kids.has(parentKey) || loadingIds.has(parentKey)) return Promise.resolve();
    setLoadingIds((prev) => new Set(prev).add(parentKey));
    return cachedRead(`loc|${siteId}|${ws}|p:${parentKey}|${reloadKey}`, () =>
      fetchProjectLocations(siteId, search, parentKey ? { parent: parentKey } : {}),
    )
      .then((page) => merge(page, parentKey))
      .finally(() =>
        setLoadingIds((prev) => {
          const next = new Set(prev);
          next.delete(parentKey);
          return next;
        }),
      );
  };

  // Arrival: the top level, then each top-level branch's children, so the
  // first screen is the design's "first level open" without reading the tree.
  useEffect(() => {
    let live = true;
    setNodes(new Map());
    setKids(new Map());
    setOpen(new Set());
    setScope(null);
    setFirst(null);
    setFailure(null);
    cachedRead(`loc|${siteId}|${ws}|p:|${reloadKey}`, () => fetchProjectLocations(siteId, search, {})).then(
      (page) => {
        if (!live) return;
        setFirst(page);
        merge(page, ROOT);
        if (page.nodes.length <= AUTO_OPEN_MAX) {
          const withKids = page.nodes.filter((node) => node.childCount > 0);
          setOpen(new Set(withKids.map((node) => node.id)));
          for (const node of withKids)
            void cachedRead(`loc|${siteId}|${ws}|p:${node.id}|${reloadKey}`, () => fetchProjectLocations(siteId, search, { parent: node.id })).then(
              (child) => live && merge(child, node.id),
            );
        }
      },
      (error) => live && setFailure(asFailure(error)),
    );
    return () => {
      live = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [siteId, ws, reloadKey]);

  useEffect(() => {
    if (!first) return;
    onMeta({ counts: `${plural(first.total, "location")} · ${plural(first.maxDepth, "level")}`, freshness: first });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [first?.total, first?.updatedAt, first?.stale, first?.error]);

  useEffect(() => {
    if (!q) {
      setFound(null);
      return;
    }
    let live = true;
    cachedRead(`loc|${siteId}|${ws}|q:${q}|${reloadKey}`, () => fetchProjectLocations(siteId, search, { q })).then(
      (page) => {
        if (!live) return;
        setFound({ q, page });
        merge(page);
      },
      () => live && setFound({ q, page: { ...(first as ProjectDetailsLocationsPage), nodes: [], matched: 0 } }),
    );
    return () => {
      live = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, siteId, ws, reloadKey]);

  // Once a picked branch's children arrive, open them too (the design's
  // "scope + its first level").
  const scopeKids = scope ? kids.get(scope) : undefined;
  useEffectOnce(scope && scopeKids ? `${scope}:${scopeKids.length}` : null, () => {
    const withKids = scopeKids!.filter((id) => (nodes.get(id)?.childCount ?? 0) > 0).slice(0, AUTO_OPEN_MAX);
    setOpen((prev) => new Set([...prev, ...withKids]));
    for (const id of withKids) void loadChildren(id);
  });

  if (!first) return failure ? <ProviderProblem error="fetch_failed" source="the server" onRetry={() => { clearDetailsCache(); setFailure(null); }} /> : <LoadingPanel />;
  if (first.error) return null;
  if (!first.total) return <ProviderEmpty text={emptyText} />;

  const roots = kids.get(ROOT) ?? [];
  const scopeNode = scope ? nodes.get(scope) : undefined;
  const inScope = scopeNode ? scopeNode.descendants : first.total;
  const searching = !!q && found?.q === q;

  // Search results are their own small tree: every match and its ancestors.
  const searchKids = new Map<string, string[]>();
  if (searching) {
    for (const node of found!.page.nodes) {
      const parent = node.parentId ?? ROOT;
      if (!searchKids.has(parent)) searchKids.set(parent, []);
      searchKids.get(parent)!.push(node.id);
    }
  }

  type Row = { node: ProjectDetailsLocationNode; rel: number; isOpen: boolean; loading: boolean };
  const rows: Row[] = [];
  const baseDepth = scopeNode ? scopeNode.depth : 0;
  const walk = (parentKey: string) => {
    const ids = searching ? searchKids.get(parentKey) ?? [] : kids.get(parentKey) ?? [];
    for (const id of ids) {
      const node = nodes.get(id);
      if (!node) continue;
      const isOpen = searching || open.has(id);
      rows.push({ node, rel: node.depth - baseDepth - 1, isOpen, loading: isOpen && !searching && loadingIds.has(id) });
      if (isOpen) walk(id);
    }
  };
  walk(searching ? (scope && searchKids.has(scope) ? scope : ROOT) : scope ?? ROOT);

  const toggle = (id: string) => {
    if (searching) return;
    const next = new Set(open);
    if (next.has(id)) next.delete(id);
    else {
      next.add(id);
      void loadChildren(id);
    }
    setOpen(next);
  };

  const pickScope = (id: string | null) => {
    setScope(id);
    if (!id) {
      setOpen(new Set(roots.filter((rootId) => (nodes.get(rootId)?.childCount ?? 0) > 0)));
      return;
    }
    void loadChildren(id).then(() => undefined);
    setOpen(new Set([id]));
  };
  /** ONE request for the whole branch, however deep — never a request per node. */
  const expandAll = () => {
    const subtree = scope ?? "root";
    cachedRead(`loc|${siteId}|${ws}|s:${subtree}|${reloadKey}`, () => fetchProjectLocations(siteId, search, { subtree })).then((page) => {
      merge(page);
      const byParent = new Map<string, string[]>();
      for (const node of page.nodes) {
        const parent = node.parentId ?? ROOT;
        if (!byParent.has(parent)) byParent.set(parent, []);
        byParent.get(parent)!.push(node.id);
      }
      setKids((prev) => {
        const next = new Map(prev);
        for (const [parent, ids] of byParent) {
          const expected = parent === ROOT ? first.nodes.length : nodes.get(parent)?.childCount ?? page.nodes.find((n) => n.id === parent)?.childCount;
          if (!page.truncated || ids.length === expected) next.set(parent, ids);
        }
        return next;
      });
      setTruncated(!!page.truncated);
      setOpen(new Set([...(scope ? [scope] : []), ...page.nodes.filter((node) => node.childCount > 0).map((node) => node.id)]));
    });
  };

  const branches = [
    { id: null as string | null, name: "All locations", count: first.total, pct: 100, fill: DS.ink, sub: plural(roots.length, "top-level branch", "top-level branches") },
    ...roots.map((id, index) => {
      const node = nodes.get(id)!;
      const max = Math.max(1, ...roots.map((r) => (nodes.get(r)?.descendants ?? 0) + 1));
      return {
        id: id as string | null,
        name: node.name,
        count: node.descendants,
        pct: Math.round(((node.descendants + 1) / max) * 100),
        fill: BRANCH_FILL[index % BRANCH_FILL.length]!,
        sub: `${node.childCount} direct · ${plural(node.descendants, "inside", "inside")}`,
      };
    }),
  ];
  const pathNames = (id: string) => {
    const out: string[] = [];
    let node = nodes.get(id);
    while (node) {
      out.unshift(node.name);
      node = node.parentId ? nodes.get(node.parentId) : undefined;
    }
    return out;
  };
  const hoverPath = hover ? pathNames(hover) : [];
  const lit = new Set<string>();
  for (let node = hover ? nodes.get(hover) : undefined; node; node = node.parentId ? nodes.get(node.parentId) : undefined) lit.add(node.id);

  return (
    <div className="pd-enter" style={{ marginTop: 20, display: "grid", gridTemplateColumns: narrow ? "minmax(0,1fr)" : "230px minmax(0,1fr)", gap: 20, alignItems: "start" }}>
      <div>
        <div style={{ ...EYEBROW, color: DS.ink }}>Branches</div>
        <div style={{ display: "flex", flexDirection: "column", gap: 2, marginTop: 10, ...(narrow ? { maxHeight: 220, overflowY: "auto" } : {}) }}>
          {branches.map((branch) => {
            const on = scope === branch.id;
            return (
              <button
                key={branch.id ?? "all"}
                type="button"
                className="pd-branch kb"
                data-testid="location-branch"
                aria-pressed={on}
                onClick={() => pickScope(branch.id)}
                style={{ display: "flex", flexDirection: "column", gap: 6, padding: "9px 12px", borderRadius: 10, background: on ? DS.surface : "transparent", border: `1px solid ${on ? "rgba(10,10,10,.12)" : "transparent"}`, cursor: "pointer", font: "inherit", textAlign: "left", color: DS.ink }}
              >
                <span style={{ display: "flex", alignItems: "center", gap: 8, width: "100%" }}>
                  <span style={{ flex: 1, minWidth: 0, fontSize: 13.5, fontWeight: 700, letterSpacing: "-.2px", ...ELLIPSIS }}>{branch.name}</span>
                  <span style={{ fontFamily: FONT_MONO, fontSize: 10, fontWeight: 600, color: DS.meta }}>{branch.count}</span>
                </span>
                <span style={{ display: "block", width: "100%", height: 4, borderRadius: 2, background: "rgba(10,10,10,.07)", overflow: "hidden" }}>
                  <span style={{ display: "block", height: "100%", width: `${branch.pct}%`, borderRadius: 2, background: branch.fill }} />
                </span>
                <span style={{ fontSize: 11.5, color: DS.meta, width: "100%", ...ELLIPSIS }}>{branch.sub}</span>
              </button>
            );
          })}
        </div>
      </div>

      <div style={{ minWidth: 0 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <SearchField testId="locations-search" height={40} value={query} onChange={setQuery} busy={!!q && !searching} placeholder={`Search ${plural(first.total, "location")}`} />
          <button type="button" className="pd-press kb" data-testid="locations-expand" onClick={expandAll} style={TOOL_BUTTON}>Expand all</button>
          <button type="button" className="pd-press kb" onClick={() => { setOpen(new Set(scope ? [scope] : [])); setTruncated(false); }} style={TOOL_BUTTON}>Collapse</button>
        </div>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, marginTop: 12 }}>
          <span style={{ ...EYEBROW, color: DS.ink, ...ELLIPSIS }}>
            {searching ? `${found!.page.matched ?? 0} MATCHING LOCATIONS` : scopeNode ? scopeNode.name.toUpperCase() : "ALL LOCATIONS"}
          </span>
          <span style={{ ...MONO_META, whiteSpace: "nowrap" }}>
            {scopeNode ? `${inScope} INSIDE` : `${plural(first.total, "LOCATION")} · ${plural(first.maxDepth, "LEVEL")}`}
          </span>
        </div>
        <div data-testid="location-tree" onMouseLeave={() => setHover(null)} style={{ marginTop: 8, border: CARD_LINE, borderRadius: 14, padding: "6px 0", background: DS.surface, maxHeight: "min(640px, calc(100vh - 360px))", overflowY: "auto" }}>
          {rows.map(({ node, rel, isOpen, loading }) => {
            const hasKids = node.childCount > 0;
            const on = lit.has(node.id);
            const pct = Math.round((node.descendants / Math.max(1, inScope)) * 100);
            return (
              <Fragment key={node.id}>
                <div
                  className="pd-tree-row"
                  data-testid="location-row"
                  role={hasKids ? "button" : undefined}
                  tabIndex={hasKids ? 0 : undefined}
                  aria-expanded={hasKids ? isOpen : undefined}
                  onMouseEnter={() => setHover(node.id)}
                  onClick={() => hasKids && toggle(node.id)}
                  onKeyDown={(event) => {
                    if (hasKids && (event.key === "Enter" || event.key === " ")) {
                      event.preventDefault();
                      toggle(node.id);
                    }
                  }}
                  style={{ position: "relative", display: "flex", alignItems: "center", gap: 8, height: 34, padding: `0 14px 0 ${14 + rel * 20}px`, background: on ? HOVER_ROW : "transparent", transition: "background .12s", cursor: hasKids ? "pointer" : "default" }}
                >
                  {Array.from({ length: Math.max(0, rel) }, (_, g) => (
                    <span key={g} aria-hidden style={{ position: "absolute", left: 21 + g * 20, top: 0, bottom: 0, width: 1, background: "rgba(10,10,10,.1)", display: "block" }} />
                  ))}
                  <span aria-hidden style={{ flex: "none", width: 16, height: 16, display: "flex", alignItems: "center", justifyContent: "center", color: DS.meta, transition: "transform .15s", transform: `rotate(${isOpen ? 90 : 0}deg)`, opacity: hasKids ? 1 : 0 }}>
                    <DsIcon name="chevron" size={12} />
                  </span>
                  <span style={{ flex: "none", fontFamily: FONT_MONO, fontSize: 8.5, fontWeight: 700, letterSpacing: ".6px", color: DS.ink, background: on ? DS.goldDeep : CHIP, padding: "2px 5px", borderRadius: 3 }}>L{node.depth}</span>
                  <span style={{ flex: 1, minWidth: 0, fontSize: 13.5, fontWeight: hasKids ? 700 : 500, letterSpacing: "-.15px", ...ELLIPSIS }}>{node.name}</span>
                  {hasKids && (
                    <>
                      <span aria-hidden style={{ flex: "none", width: 72, height: 4, borderRadius: 2, background: "rgba(10,10,10,.06)", overflow: "hidden" }}>
                        <span style={{ display: "block", height: "100%", width: `${pct}%`, borderRadius: 2, background: DS.treeBar }} />
                      </span>
                      <span style={{ flex: "none", width: 34, textAlign: "right", fontFamily: FONT_MONO, fontSize: 10, color: DS.meta }}>{node.descendants}</span>
                    </>
                  )}
                </div>
                {loading && (
                  <div style={{ display: "flex", alignItems: "center", gap: 8, height: 30, padding: `0 14px 0 ${38 + (rel + 1) * 20}px`, fontSize: 12.5, color: DS.meta }}>
                    <Spinner size={12} /> Loading…
                  </div>
                )}
              </Fragment>
            );
          })}
          {rows.length === 0 && (
            <div style={{ padding: "28px 16px", textAlign: "center", fontSize: 13.5, color: DS.meta }}>
              {q ? (searching ? `No locations match “${q}”` : "Searching…") : scope && loadingIds.has(scope) ? "Loading…" : "Nothing inside this branch."}
            </div>
          )}
        </div>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, marginTop: 10, fontSize: 12.5, color: DS.meta }}>
          <span data-testid="location-path" style={{ minWidth: 0, ...ELLIPSIS }}>
            {hoverPath.length
              ? hoverPath.join(" › ")
              : truncated || (searching && found!.page.truncated)
                ? `Showing the first ${MAX_TREE_ROWS.toLocaleString()} — search to narrow it down`
                : "Hover a row to see its full path · click a branch to open it"}
          </span>
          <span style={{ fontFamily: FONT_MONO, fontSize: 9.5, letterSpacing: ".8px", whiteSpace: "nowrap" }}>SHOWING {rows.length} OF {inScope}</span>
        </div>
      </div>
    </div>
  );
}

/** The server caps a search or subtree at this many rows. */
const MAX_TREE_ROWS = 2000;

/** Run `effect` once each time `key` takes a new non-null value. */
function useEffectOnce(key: string | null, effect: () => void) {
  const last = useRef<string | null>(null);
  useEffect(() => {
    if (key === null || key === last.current) return;
    last.current = key;
    effect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);
}

const TOOL_BUTTON: React.CSSProperties = {
  height: 40,
  padding: "0 12px",
  borderRadius: 12,
  border: FIELD_LINE,
  background: DS.surface,
  display: "inline-flex",
  alignItems: "center",
  font: "inherit",
  fontSize: 12.5,
  fontWeight: 700,
  color: DS.ink,
  cursor: "pointer",
  whiteSpace: "nowrap",
};

// ---------------------------------------------------------------------------
// Import review — the two modals
// ---------------------------------------------------------------------------

function Modal({ width, label, onClose, children }: { width: number; label: string; onClose: () => void; children: React.ReactNode }) {
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);
  // Portaled: the shell's view wrapper animates, which makes it a stacking
  // context, and the Ask composer would otherwise draw over the modal.
  return createPortal(
    <div className="pd-scrim" onClick={onClose} style={{ position: "fixed", inset: 0, zIndex: 40, background: "rgba(10,10,10,.42)", display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
      <div
        role="dialog"
        aria-modal="true"
        aria-label={label}
        data-testid="project-file-modal"
        className="pd-modal"
        onClick={(event) => event.stopPropagation()}
        style={{ width, maxWidth: "100%", maxHeight: "calc(100vh - 32px)", display: "flex", flexDirection: "column", background: DS.surface, borderRadius: 22, boxShadow: "0 30px 80px rgba(10,10,10,.28)", overflow: "hidden" }}
      >
        {children}
      </div>
    </div>,
    document.body,
  );
}

function ModalHead({ eyebrow, title, meta, onClose, children }: { eyebrow: string; title: string; meta: string; onClose: () => void; children?: React.ReactNode }) {
  return (
    <div style={{ flex: "none", padding: "24px 28px 0" }}>
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 16 }}>
        <div style={{ minWidth: 0 }}>
          <div style={EYEBROW}>{eyebrow}</div>
          <div style={{ fontSize: 24, fontWeight: 800, letterSpacing: "-.7px", marginTop: 6, ...ELLIPSIS }}>{title}</div>
          <div style={{ fontSize: 13.5, color: DS.body2, marginTop: 4 }}>{meta}</div>
        </div>
        <button type="button" aria-label="Close" className="pd-press kb" onClick={onClose} style={{ flex: "none", width: 36, height: 36, border: 0, borderRadius: 18, background: DS.paper, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer" }}>
          <DsIcon name="close" size={15} color={DS.ink} />
        </button>
      </div>
      {children}
    </div>
  );
}

function ModalFoot({ note, children }: { note: string; children: React.ReactNode }) {
  return (
    <div style={{ flex: "none", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, padding: "16px 28px", borderTop: CARD_LINE, background: SOFT, flexWrap: "wrap" }}>
      <span style={{ minWidth: 0, fontSize: 13, color: DS.meta }}>{note}</span>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", justifyContent: "flex-end", maxWidth: "100%" }}>{children}</div>
    </div>
  );
}

function PillButton({ children, onClick, tone = "secondary", busy, testId }: { children: React.ReactNode; onClick: () => void; tone?: "secondary" | "outline" | "gold"; busy?: boolean; testId?: string }) {
  const look: React.CSSProperties =
    tone === "gold"
      ? { background: GOLD_BTN, boxShadow: GOLD_SHADOW, border: 0, fontWeight: 800 }
      : tone === "outline"
        ? { background: DS.surface, boxShadow: `inset 0 0 0 1.5px ${DS.ink}`, border: 0, fontWeight: 800 }
        : { background: DS.surface, border: FIELD_LINE, fontWeight: 700 };
  return (
    <button
      type="button"
      className="pd-press kb"
      data-testid={testId}
      disabled={busy}
      onClick={onClick}
      style={{ height: 44, padding: "0 20px", borderRadius: 22, display: "flex", alignItems: "center", gap: 8, whiteSpace: "nowrap", font: "inherit", fontSize: 14.5, color: DS.ink, cursor: busy ? "default" : "pointer", ...look }}
    >
      {busy && <Spinner />}
      {children}
    </button>
  );
}

function Spinner({ size = 16 }: { size?: number }) {
  return <span aria-hidden style={{ flex: "none", width: size, height: size, boxSizing: "border-box", borderRadius: size / 2, border: `${size > 13 ? 2.5 : 2}px solid rgba(10,10,10,.15)`, borderTopColor: DS.ink, display: "block" }} className="pd-spin" />;
}

function FileWarnings({ warnings }: { warnings: ProjectFileValidation["warnings"] }) {
  if (!warnings.length) return null;
  return <div role="status" data-testid="file-warnings" style={{ marginBottom: 18, padding: "12px 16px", borderRadius: 12, background: DS.warnSoft, color: DS.warnDeep, fontSize: 13, lineHeight: 1.5 }}>
    <strong>Check these warnings</strong>
    <ul style={{ margin: "6px 0 0", paddingLeft: 18 }}>{warnings.map((warning, index) => <li key={index}>{warning.row ? `Row ${warning.row}: ` : ""}{warning.column ? `${warning.column}: ` : ""}{warning.message}</li>)}</ul>
  </div>;
}

function fileFormat(filename?: string) {
  return filename?.split(".").at(-1)?.toUpperCase() || "FILE";
}

function DirectoryReview({
  directory,
  filename,
  warnings,
  review,
  saving,
  onClose,
  onBack,
  onConfirm,
}: {
  directory: ProjectDetailsDirectory;
  filename: string;
  warnings: ProjectFileValidation["warnings"];
  review: boolean;
  saving: boolean;
  onClose: () => void;
  /** Review only: back to step 1 with the same file chosen. */
  onBack: () => void;
  onConfirm: () => void;
}) {
  const model = useMemo(() => buildDirectory(directory), [directory]);
  const [step, setStep] = useState<1 | 2>(1);
  const [filter, setFilter] = useState<"all" | "issues">("all");
  const [hover, setHover] = useState<string | null>(null);
  const [query, setQuery] = useState("");
  const [limit, setLimit] = useState(PAGE);
  useEffect(() => setLimit(PAGE), [query, hover, filter]);
  const pool = model.people.filter(
    (person) => matchesPerson(person, query) && (!hover || person.bucket === hover) && (filter === "all" || !person.groupId || !person.role),
  );
  const hovered = hover ? model.crew.find((segment) => segment.id === hover) : undefined;
  const counts = `${plural(model.people.length, "person", "people")} · ${plural(model.companies.length, "company", "companies")}`;
  const top = model.companies.filter((company) => company.top);
  const rest = model.companies.filter((company) => !company.top && company.count > 0);
  const restCount = rest.reduce((sum, company) => sum + company.count, 0);
  const tiles = [
    ...top.map((company) => ({ name: company.name, fill: company.fill, fg: company.fg, count: company.count, roles: company.roles })),
    ...(restCount
      ? [{ name: `${rest.length} other ${rest.length === 1 ? "company" : "companies"}`, fill: OTHER_FILL, fg: DS.ink, count: restCount, roles: rest.slice(0, 3).map((c) => c.name).join(", ") + (rest.length > 3 ? ` + ${rest.length - 3} more` : "") }]
      : []),
  ];
  const steps = [
    { n: 1 as const, label: "People & roles", count: model.people.length },
    { n: 2 as const, label: "Companies", count: model.companies.length },
  ];

  return (
    <Modal width={880} label="Directory import review" onClose={onClose}>
      <ModalHead
        eyebrow={review ? "PREVIEW · STEP 2 OF 2 · NOTHING SAVED YET" : "CURRENT FILE · PROJECT DIRECTORY"}
        title={filename}
        meta={counts}
        onClose={onClose}
      >
        <div role="tablist" style={{ display: "flex", gap: 24, marginTop: 20, borderBottom: CARD_LINE }}>
          {steps.map((s) => {
            const on = step === s.n;
            return (
              <button
                key={s.n}
                type="button"
                role="tab"
                aria-selected={on}
                className="kb"
                onClick={() => {
                  setStep(s.n);
                  setHover(null);
                }}
                style={{ display: "flex", alignItems: "center", gap: 9, padding: "0 0 13px", border: 0, borderBottom: `2px solid ${on ? DS.ink : "transparent"}`, marginBottom: -1, background: "transparent", cursor: "pointer", font: "inherit" }}
              >
                <span style={{ whiteSpace: "nowrap", fontSize: 14.5, fontWeight: 700, letterSpacing: "-.2px", color: on ? DS.ink : DS.meta }}>{s.label}</span>
                <span style={{ fontFamily: FONT_MONO, fontSize: 10, fontWeight: 600, letterSpacing: "1px", color: DS.dashed }}>{s.count}</span>
              </button>
            );
          })}
        </div>
      </ModalHead>
      <div style={{ flex: 1, minHeight: 0, maxHeight: "calc(100vh - 220px)", overflowY: "auto", padding: "20px 28px 24px" }}>
        <FileWarnings warnings={warnings} />
        {step === 1 ? (
          <div className="pd-enter">
            <CrewBar crew={model.crew} hover={hover} onHover={setHover} />
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, marginTop: 22, flexWrap: "wrap" }}>
              <span style={{ ...EYEBROW, color: DS.ink }}>
                {hovered ? `${hovered.name.toUpperCase()} · ${pool.length}` : query || filter === "issues" ? `${pool.length} MATCHES` : `${model.people.length.toLocaleString()} PEOPLE · A–Z`}
              </span>
              <Segmented
                value={filter}
                onChange={setFilter}
                track={CHIP}
                options={[
                  { id: "all", label: `All ${model.people.length}` },
                  { id: "issues", label: `Missing info · ${model.missingInfo}`, warn: true },
                ]}
              />
            </div>
            <div style={{ display: "flex", marginTop: 10 }}>
              <SearchField height={40} value={query} onChange={setQuery} placeholder={`Search ${plural(model.people.length, "person", "people")} by name, role or company`} />
            </div>
            <div style={{ marginTop: 8 }}>
              <PeopleTable
                companies={model.companies}
                people={pool.slice(0, limit)}
                footer={pool.length > limit && <MoreButton shown={limit} of={pool.length} onMore={() => setLimit((n) => n + PAGE)} />}
              />
            </div>
          </div>
        ) : (
          <div className="pd-enter">
            <span style={{ ...EYEBROW, color: DS.ink }}>TOP {top.length} OF {model.companies.length} COMPANIES · SIZED BY CREW</span>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 14 }}>
              {tiles.map((tile) => (
                <div key={tile.name} style={{ flex: `${tile.count} 1 ${Math.round(tile.count * 5.5)}px`, minWidth: 118, height: tile.count >= 50 ? 132 : 104, borderRadius: 14, background: tile.fill, color: tile.fg, padding: "13px 14px", display: "flex", flexDirection: "column", justifyContent: "space-between", overflow: "hidden", boxSizing: "border-box" }}>
                  <div>
                    <div style={{ fontSize: 14.5, fontWeight: 800, letterSpacing: "-.3px", lineHeight: 1.2 }}>{tile.name}</div>
                    <div style={{ fontSize: 12, fontWeight: 500, opacity: 0.78, marginTop: 4, ...ELLIPSIS }}>{tile.roles}</div>
                  </div>
                  <div style={{ display: "flex", alignItems: "baseline", gap: 6 }}>
                    <span style={{ fontSize: 26, fontWeight: 800, letterSpacing: "-1px", lineHeight: 1 }}>{tile.count}</span>
                    <span style={{ fontFamily: FONT_MONO, fontSize: 9.5, fontWeight: 700, letterSpacing: "1.1px", opacity: 0.75 }}>PEOPLE</span>
                  </div>
                </div>
              ))}
            </div>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, marginTop: 24 }}>
              <span style={{ ...EYEBROW, color: DS.ink }}>ALL {model.companies.length} COMPANIES</span>
              <span style={{ fontSize: 12.5, color: DS.meta }}>Largest first</span>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(250px,1fr))", columnGap: 24, marginTop: 8, borderTop: CARD_LINE }}>
              {model.companies.map((company) => (
                <div key={company.id} style={{ display: "flex", alignItems: "center", gap: 9, height: 36, borderBottom: ROW_LINE }}>
                  <span aria-hidden style={{ flex: "none", width: 8, height: 8, borderRadius: 2, background: company.fill, display: "block" }} />
                  <span style={{ flex: 1, minWidth: 0, fontSize: 13.5, fontWeight: company.top ? 700 : 500, ...ELLIPSIS }}>{company.name}</span>
                  <span style={{ flex: "none", fontFamily: FONT_MONO, fontSize: 11, color: DS.meta }}>{company.count}</span>
                </div>
              ))}
            </div>
            {model.noCompany > 0 && (
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 14, padding: "12px 14px", borderRadius: 12, background: DS.warnSoft }}>
                <span aria-hidden style={{ width: 8, height: 8, borderRadius: 4, background: DS.warnDot, display: "block", flex: "none" }} />
                <span style={{ fontSize: 13.5, lineHeight: 1.45, color: DS.warnDeep }}>
                  <strong>{plural(model.noCompany, "person has", "people have")} no company.</strong> They'll still be matched by name, just not grouped with a crew.
                </span>
              </div>
            )}
          </div>
        )}
      </div>
      <ModalFoot note={review ? "Step 2 of 2 · nothing is saved yet" : "Viewing the file in use"}>
        <PillButton onClick={step === 2 ? () => setStep(1) : review ? onBack : onClose}>{step === 2 ? "Back" : review ? "Back to upload" : "Close"}</PillButton>
        {step === 1 && (
          <PillButton tone="outline" testId="review-next" onClick={() => setStep(2)}>
            Next · Companies <DsIcon name="arrowRight" size={14} color={DS.ink} />
          </PillButton>
        )}
        {step === 2 && review && (
          <PillButton tone="gold" testId="review-confirm" busy={saving} onClick={onConfirm}>
            Use this file <DsIcon name="arrowRight" size={14} color={DS.ink} />
          </PillButton>
        )}
      </ModalFoot>
    </Modal>
  );
}

function LocationsReview({
  locations,
  filename,
  warnings,
  review,
  saving,
  onClose,
  onBack,
  onConfirm,
}: {
  locations: ProjectDetailsLocation[];
  filename: string;
  warnings: ProjectFileValidation["warnings"];
  review: boolean;
  saving: boolean;
  onClose: () => void;
  onBack: () => void;
  onConfirm: () => void;
}) {
  const tree = useMemo(() => buildLocationTree(locations), [locations]);
  const [hover, setHover] = useState<string | null>(null);
  const lit = new Set(hover ? pathTo(tree, hover) : []);
  const rows = visibleRows(tree, null, new Set(tree.byId.keys()), "");
  const levels = Array.from({ length: tree.maxDepth }, (_, i) => i + 1);
  const meta = `${plural(tree.total, "location")} · ${plural(tree.maxDepth, "level")}`;
  return (
    <Modal width={980} label="Locations import review" onClose={onClose}>
      <ModalHead eyebrow={review ? "PREVIEW · STEP 2 OF 2 · NOTHING SAVED YET" : "CURRENT FILE · LOCATION HIERARCHY"} title={filename} meta={meta} onClose={onClose}>
        <div style={{ height: 18, borderBottom: CARD_LINE, margin: "0 -28px" }} />
      </ModalHead>
      <div style={{ flex: 1, minHeight: 0, maxHeight: "calc(100vh - 160px)", overflowY: "auto", padding: "22px 28px 26px" }}>
        <FileWarnings warnings={warnings} />
        <div style={{ border: CARD_LINE, borderRadius: 14, padding: "6px 0" }} onMouseLeave={() => setHover(null)}>
          {rows.map(({ node }) => {
            const rel = node.depth - 1;
            const on = lit.has(node.id);
            return (
              <div key={node.id} onMouseEnter={() => setHover(node.id)} style={{ position: "relative", display: "flex", alignItems: "center", gap: 8, height: 34, padding: `0 14px 0 ${14 + rel * 20}px`, background: on ? HOVER_ROW : "transparent", transition: "background .12s" }}>
                {Array.from({ length: Math.max(0, rel - 1) }, (_, g) => (
                  <span key={g} aria-hidden style={{ position: "absolute", left: 23 + g * 20, top: 0, bottom: 0, width: 1, background: "rgba(10,10,10,.12)", display: "block" }} />
                ))}
                {rel > 0 && <span aria-hidden style={{ position: "absolute", left: 23 + (rel - 1) * 20, top: "50%", width: 9, height: 1, background: "rgba(10,10,10,.18)", display: "block" }} />}
                <span style={{ fontFamily: FONT_MONO, fontSize: 8.5, fontWeight: 700, letterSpacing: ".6px", color: DS.ink, background: on ? DS.goldDeep : CHIP, padding: "2px 5px", borderRadius: 3 }}>L{node.depth}</span>
                <span style={{ flex: 1, minWidth: 0, fontSize: 13.5, fontWeight: node.children.length ? 700 : 500, letterSpacing: "-.15px", ...ELLIPSIS }}>{node.name}</span>
                {node.children.length > 0 && <span style={{ fontFamily: FONT_MONO, fontSize: 10, color: DS.dashed }}>{node.children.length}</span>}
              </div>
            );
          })}
        </div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: "6px 16px", marginTop: 12 }}>
          {levels.map((depth) => (
            <span key={depth} style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: DS.body2 }}>
              <span style={{ fontFamily: FONT_MONO, fontSize: 9.5, fontWeight: 700, letterSpacing: ".8px", color: DS.ink, background: CHIP, padding: "2px 6px", borderRadius: 4 }}>L{depth}</span>
              {levelLabel(depth)}
            </span>
          ))}
        </div>
      </div>
      <ModalFoot note={review ? "Nothing is saved until you confirm" : "Viewing the file in use"}>
        <PillButton onClick={review ? onBack : onClose}>{review ? "Back to upload" : "Close"}</PillButton>
        {review && (
          <PillButton tone="gold" testId="review-confirm" busy={saving} onClick={onConfirm}>
            Use this file
          </PillButton>
        )}
      </ModalFoot>
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// Upload (a Led AI project with no file yet, or replacing one)
// ---------------------------------------------------------------------------

/**
 * The templates the guide offers. The directory template is the SIMPLE format
 * (`SIMPLE_DIRECTORY_COLUMNS` in the backend parser) — the shape any file that
 * is not Procore's own export needs, an Autodesk member list included. A
 * Procore user never needs a template: the export uploads as it downloads.
 */
const TEMPLATES: Record<ProjectDetailsTab, { name: string; body: string }> = {
  directory: {
    name: "Led AI directory template.csv",
    body: "Name,Company,Job Title,Email,Trade\nAlex Rivera,Example Electrical,Electrical Foreman,alex@example.com,Electrical\nSam Lee,Example Mechanical,Pipefitter,,Mechanical\nJordan Park,,Superintendent,jordan@example.com,\n",
  },
  locations: {
    name: "Led AI locations template.csv",
    body: "Building A\nBuilding A > Level 01\nBuilding A > Level 01 > Electrical Room\nBuilding A > Level 02 > Unit 201\nSite > Garage\n",
  },
};

function downloadTemplate(tab: ProjectDetailsTab) {
  const template = TEMPLATES[tab];
  const url = URL.createObjectURL(new Blob([template.body], { type: "text/csv" }));
  const link = document.createElement("a");
  link.href = url;
  link.download = template.name;
  link.click();
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

type ExportSource = "procore" | "autodesk" | "buildertrend";
/** The sources the guide offers. Buildertrend's steps are kept but hidden
 *  until it is offered (user-directed, 2026-10-23). */
const VISIBLE_SOURCES: Array<{ id: ExportSource; label: string }> = [
  { id: "procore", label: "Procore" },
  { id: "autodesk", label: "Autodesk" },
];
const SOURCE_KEY = "ledai.projectDetails.exportSource";
function rememberedSource(): ExportSource {
  try {
    const saved = localStorage.getItem(SOURCE_KEY);
    return VISIBLE_SOURCES.find((source) => source.id === saved)?.id ?? "procore";
  } catch {
    return "procore";
  }
}

/**
 * Where each source's file comes from, step by step. Procore's steps are its
 * own documented exports (Directory → Users → Export → CSV; Admin → Locations
 * → Export → CSV), which are the exact files this parser was built against.
 * Autodesk's Project Admin Download report produces XLSX files read directly. Buildertrend documents no
 * export of either, so its (hidden) steps build the file from our template.
 */
const STEPS: Record<ExportSource, Record<ProjectDetailsTab, { lead?: string; steps: React.ReactNode[]; note?: string }>> = {
  procore: {
    directory: {
      steps: [
        <>Open the project in Procore and go to <b>Directory</b>.</>,
        <>On the <b>Users</b> tab, clear any search or filters — the export only includes the people showing.</>,
        <>Click <b>Export</b>, choose <b>CSV</b>, and upload that file as it is.</>,
      ],
      note: "Needs Read Only access or higher on the Directory tool.",
    },
    locations: {
      steps: [
        <>Open the project in Procore and go to <b>Admin → Locations</b>.</>,
        <>Click <b>Export</b> and choose <b>CSV</b>.</>,
        <>Upload the file as it is — Led AI joins Procore's tier columns into one path.</>,
      ],
      note: "Needs Admin on the Admin tool, or the Manage Locations permission.",
    },
  },
  autodesk: {
    directory: {
      steps: [
        <>Open the project in Autodesk Construction Cloud and go to <b>Project Admin → Members</b>.</>,
        <>Clear any filters so every member is listed.</>,
        <>Click <b>Export → Run report</b>. When ready, choose <b>Download report</b> and upload the <b>.xlsx</b> file as it is.</>,
      ],
      note: "Needs Project Admin access in ACC.",
    },
    locations: {
      steps: [
        <>Open the project in Autodesk Construction Cloud and go to <b>Project Admin → Locations</b>.</>,
        <>Click <b>Export</b> to download the <b>.xlsx</b> file. If prompted, choose <b>Download report</b>.</>,
        <>Upload the file as it is — <b>Tier 1, Tier 2, Tier 3</b> form the hierarchy. Location ID and Barcode are ignored.</>,
      ],
      note: "Needs Project Admin access in ACC.",
    },
  },
  buildertrend: {
    directory: {
      lead: "Buildertrend doesn't export a project directory, so build one from our template:",
      steps: [
        <><b>Download the template</b> below and open it in Excel or Google Sheets.</>,
        <>Add a row for each person on this job — your team from <b>Internal Users</b> and your trade partners from <b>Subs/Vendors</b>, both under the <b>Users</b> menu.</>,
        <>Save as <b>CSV</b> (File → Save As / Download → .csv) and upload it here.</>,
      ],
    },
    locations: {
      lead: "Buildertrend has no location list to export, so type yours into our template:",
      steps: [
        <><b>Download the template</b> below and open it in Excel or Google Sheets.</>,
        <>One location per line, the full path from the top, levels separated by <b>&gt;</b> — the areas you already use for Selections are a good start.</>,
        <>Save as <b>CSV</b> and upload it here.</>,
      ],
    },
  },
};

const SOURCE_NAME: Record<ExportSource, string> = { procore: "Procore", autodesk: "Autodesk", buildertrend: "Buildertrend" };
const PATH_SAMPLE = "Tower A\nTower A > Level 01\nTower A > Level 01 > Lobby\nTower A > Level 01 > Electrical Room\nSite > Loading Dock";

function ExpectedFormat({ tab, source }: { tab: ProjectDetailsTab; source: ExportSource }) {
  const name = SOURCE_NAME[source];
  if (tab === "locations") {
    return (
      <div data-testid="expected-format">
        <div style={{ fontSize: 13.5, lineHeight: 1.5, color: INK3, padding: "12px 0 10px" }}>
          {name}'s export uploads as it is.{source === "autodesk" ? " XLSX or CSV tier columns form the hierarchy; IDs and barcodes are ignored." : ""} For a custom CSV, use one full path per line:
        </div>
        <pre style={{ margin: 0, padding: "12px 14px", border: CARD_LINE, borderRadius: 10, background: HEAD_BG, fontFamily: FONT_MONO, fontSize: 12, lineHeight: 1.7, color: DS.ink, overflowX: "auto", whiteSpace: "pre" }}>{PATH_SAMPLE}</pre>
        <div style={{ fontSize: 12.5, lineHeight: 1.5, color: DS.meta, marginTop: 10 }}>
          Separate levels with " &gt; ". Parents are created automatically if a line skips them. Any depth works.
        </div>
      </div>
    );
  }
  const columns: Array<[string, boolean, string]> = [
    ["Name", true, "Alex Rivera"],
    ["Company", false, "Example Electrical"],
    ["Job Title", false, "Electrical Foreman"],
    ["Email", false, "alex@example.com"],
    ["Trade", false, "Electrical"],
  ];
  const grid = "minmax(0,1fr) 84px minmax(0,1.2fr)";
  return (
    <div data-testid="expected-format">
      <div style={{ fontSize: 13.5, lineHeight: 1.5, color: INK3, padding: "12px 0 10px" }}>
        {name}'s export uploads as it is. Any other CSV needs a header row with these columns, in any order:
      </div>
      <div style={{ border: CARD_LINE, borderRadius: 10, overflow: "hidden" }}>
        <div style={{ display: "grid", gridTemplateColumns: grid, gap: 12, padding: "8px 14px", background: HEAD_BG, borderBottom: "1px solid rgba(10,10,10,.07)", fontFamily: FONT_MONO, fontSize: 9, fontWeight: 700, letterSpacing: "1.3px", color: DS.meta }}>
          <span>COLUMN</span><span /><span>EXAMPLE</span>
        </div>
        {columns.map(([col, required, example], i) => (
          <div key={col} style={{ display: "grid", gridTemplateColumns: grid, gap: 12, alignItems: "center", padding: "9px 14px", borderTop: i ? ROW_LINE : "none" }}>
            <span style={{ fontSize: 13.5, fontWeight: 700, letterSpacing: "-.2px" }}>{col}</span>
            <span style={{ fontFamily: FONT_MONO, fontSize: 8.5, fontWeight: 700, letterSpacing: "1px", color: required ? WARN_INK : DS.metaQuiet }}>{required ? "REQUIRED" : "OPTIONAL"}</span>
            <span style={{ fontSize: 13, color: DS.body2, ...ELLIPSIS }}>{example}</span>
          </div>
        ))}
      </div>
      <div style={{ fontSize: 12.5, lineHeight: 1.5, color: DS.meta, marginTop: 10 }}>
        First Name and Last Name work in place of Name; Role works for Job Title. Other columns are ignored.
      </div>
    </div>
  );
}

const formatBytes = (bytes: number) => (bytes < 1024 ? `${bytes} B` : bytes < 1024 * 1024 ? `${(bytes / 1024).toFixed(1)} KB` : `${(bytes / 1024 / 1024).toFixed(1)} MB`);

/** The empty page: one card that opens the upload, and the way to the guide. */
function EmptyUpload({ tab, onOpen, onHelp }: { tab: ProjectDetailsTab; onOpen: () => void; onHelp: () => void }) {
  const isDir = tab === "directory";
  return (
    <div className="pd-enter" style={{ marginTop: 28, display: "flex", flexDirection: "column", alignItems: "center" }}>
      <button
        type="button"
        className="pd-drop kb"
        data-testid="project-file-drop"
        onClick={onOpen}
        style={{ width: "100%", maxWidth: 640, border: "1.5px dashed rgba(10,10,10,.18)", borderRadius: 20, background: SOFT, padding: "44px 32px 36px", display: "flex", flexDirection: "column", alignItems: "center", textAlign: "center", cursor: "pointer", font: "inherit", color: DS.ink }}
      >
        <span style={{ width: 56, height: 56, borderRadius: 16, background: DS.surface, border: "1px solid rgba(10,10,10,.09)", display: "flex", alignItems: "center", justifyContent: "center" }}>
          <DsIcon name={isDir ? "person" : "pin"} size={22} color={DS.ink} />
        </span>
        <span style={{ fontSize: 22, fontWeight: 800, letterSpacing: "-.6px", marginTop: 18 }}>{isDir ? "No directory yet" : "No locations yet"}</span>
        <span style={{ fontSize: 14.5, lineHeight: 1.5, color: DS.body2, marginTop: 6, maxWidth: 400 }}>
          {isDir ? "Upload a directory export and Led AI will match names, roles and companies." : "Upload your location list and Led AI will build the hierarchy."}
        </span>
        <span style={{ marginTop: 22, height: 48, padding: "0 24px", borderRadius: 24, background: DS.ink, color: DS.surface, display: "inline-flex", alignItems: "center", gap: 9, fontSize: 15, fontWeight: 800, boxShadow: "0 10px 24px rgba(10,10,10,.18)" }}>
          <UploadGlyph />
          Upload CSV or XLSX
        </span>
        <span style={{ fontFamily: FONT_MONO, fontSize: 9.5, fontWeight: 600, letterSpacing: "1.3px", color: DS.metaQuiet, marginTop: 16 }}>
          OR DROP A FILE HERE · {isDir ? "NAME, ROLE, COMPANY" : "TIERS OR ONE PATH PER LINE"}
        </span>
      </button>
      <div style={{ display: "flex", alignItems: "center", gap: 14, marginTop: 16, fontSize: 13, color: DS.meta, flexWrap: "wrap", justifyContent: "center" }}>
        <span>Nothing is saved until you review what's inside.</span>
        <span aria-hidden style={{ color: DS.segDot }}>·</span>
        <button type="button" className="pd-link kb" data-testid="upload-help" onClick={onHelp} style={{ ...LINK_BUTTON, textDecoration: "underline", textUnderlineOffset: 3 }}>
          Where do I get this file?
        </button>
      </div>
    </div>
  );
}

/**
 * Step 1 of 2: where to get the file, what it must look like, and the file
 * itself. "Check file & preview" sends it to the server's validate-only parse;
 * step 2 is the review modal, and nothing is saved before its "Use this file".
 */
function UploadModal({
  tab,
  picked,
  validating,
  error,
  helpOpen,
  onHelpToggle,
  onPick,
  onClearPick,
  onValidate,
  onClose,
}: {
  tab: ProjectDetailsTab;
  picked: File | null;
  validating: boolean;
  error: ProjectFileError | null;
  helpOpen: boolean;
  onHelpToggle: () => void;
  onPick: (file: File) => void;
  onClearPick: () => void;
  onValidate: () => void;
  onClose: () => void;
}) {
  const isDir = tab === "directory";
  const input = useRef<HTMLInputElement>(null);
  const [dragging, setDragging] = useState(false);
  const [source, setSource] = useState<ExportSource>(rememberedSource);
  const pickSource = (next: ExportSource) => {
    setSource(next);
    try {
      localStorage.setItem(SOURCE_KEY, next);
    } catch {
      // A private window: the choice just isn't remembered.
    }
  };
  const guide = STEPS[source][tab];
  const ready = !!picked && !validating;
  return (
    <Modal width={720} label="Upload CSV or XLSX" onClose={onClose}>
      <ModalHead
        eyebrow="UPLOAD · STEP 1 OF 2"
        title={isDir ? "Import the project directory" : "Import the location hierarchy"}
        meta={isDir ? "People, companies and roles. Led AI checks the file and shows you a preview before anything is saved." : "Buildings, floors and rooms. Led AI checks the file and shows you a preview before anything is saved."}
        onClose={onClose}
      />
      <div style={{ flex: 1, minHeight: 0, maxHeight: "calc(100vh - 220px)", overflowY: "auto", padding: "20px 28px 8px" }}>
        <input
          ref={input}
          type="file"
          accept=".csv,.xlsx,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
          data-testid="project-file-input"
          style={{ display: "none" }}
          onChange={(event) => {
            const file = event.target.files?.[0];
            if (file) onPick(file);
            event.target.value = "";
          }}
        />
        {/* Where to get it — open by default, folded away once you know. */}
        <div data-testid="upload-guide" style={{ border: CARD_LINE, borderRadius: 14, overflow: "hidden" }}>
          <div
            role="button"
            tabIndex={0}
            aria-expanded={helpOpen}
            className="pd-row"
            onClick={onHelpToggle}
            onKeyDown={(event) => (event.key === "Enter" || event.key === " ") && (event.preventDefault(), onHelpToggle())}
            style={{ display: "flex", alignItems: "center", gap: 12, height: 50, padding: "0 16px", cursor: "pointer", background: helpOpen ? DS.surface : SOFT }}
          >
            <span style={{ ...EYEBROW, color: DS.ink }}>Where to get this file</span>
            <span style={{ flex: 1 }} />
            {helpOpen && (
              <div onClick={(event) => event.stopPropagation()}>
                <Segmented
                  testId="upload-source"
                  value={source}
                  onChange={pickSource}
                  options={VISIBLE_SOURCES.map((s) => ({ ...s, mark: s.id }))}
                />
              </div>
            )}
            <DsIcon name="chevron" size={13} color={DS.meta} style={{ transform: `rotate(${helpOpen ? -90 : 90}deg)`, transition: "transform .15s" }} />
          </div>
          {helpOpen && (
            <div style={{ padding: "4px 16px 14px", borderTop: "1px solid rgba(10,10,10,.06)" }}>
              {guide.lead && <div style={{ fontSize: 13.5, color: DS.body2, padding: "8px 0 0" }}>{guide.lead}</div>}
              <ol data-testid="upload-steps" style={{ margin: 0, padding: 0, listStyle: "none" }}>
                {guide.steps.map((step, i) => (
                  <li key={i} style={{ display: "flex", alignItems: "flex-start", gap: 10, padding: "8px 0", fontSize: 13.5, lineHeight: 1.5, color: INK3 }}>
                    <span aria-hidden style={{ flex: "none", width: 20, height: 20, marginTop: 1, borderRadius: 10, background: DS.ink, color: DS.surface, display: "flex", alignItems: "center", justifyContent: "center", fontFamily: FONT_MONO, fontSize: 10, fontWeight: 700 }}>{i + 1}</span>
                    <span>{step}</span>
                  </li>
                ))}
              </ol>
              {guide.note && <div style={{ fontSize: 12.5, color: DS.meta, marginTop: 4, paddingLeft: 30 }}>{guide.note}</div>}
            </div>
          )}
        </div>

        {/* Expected format */}
        <div style={{ marginTop: 12, border: CARD_LINE, borderRadius: 14, overflow: "hidden" }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, height: 50, padding: "0 16px" }}>
            <span style={{ ...EYEBROW, color: DS.ink }}>Expected format</span>
            <button type="button" className="pd-link kb" data-testid="download-template" onClick={() => downloadTemplate(tab)} style={{ ...LINK_BUTTON, display: "inline-flex", alignItems: "center", gap: 6 }}>
              <span style={{ display: "inline-flex", transform: "rotate(180deg)" }}><UploadGlyph color={DS.ink} /></span>
              Download template
            </button>
          </div>
          <div style={{ padding: "0 16px 14px", borderTop: "1px solid rgba(10,10,10,.06)" }}>
            <ExpectedFormat tab={tab} source={source} />
          </div>
        </div>

        {/* The file */}
        {!picked ? (
          <button
            type="button"
            className="pd-drop kb"
            data-testid="project-file-choose"
            data-dragging={dragging}
            onClick={() => input.current?.click()}
            onDragOver={(event) => {
              event.preventDefault();
              setDragging(true);
            }}
            onDragLeave={() => setDragging(false)}
            onDrop={(event) => {
              event.preventDefault();
              setDragging(false);
              const file = event.dataTransfer.files?.[0];
              if (file) onPick(file);
            }}
            style={{ width: "100%", marginTop: 12, border: "1.5px dashed rgba(10,10,10,.22)", borderRadius: 14, padding: "26px 20px", display: "flex", flexDirection: "column", alignItems: "center", gap: 6, textAlign: "center", cursor: "pointer", background: SOFT, font: "inherit", color: DS.ink }}
          >
            <span style={{ fontSize: 15, fontWeight: 700, letterSpacing: "-.2px" }}>Drop your CSV or XLSX here, or <u>browse</u></span>
            <span style={{ fontFamily: FONT_MONO, fontSize: 9.5, fontWeight: 600, letterSpacing: "1.2px", color: DS.metaQuiet }}>{isDir ? "NAME, ROLE, COMPANY" : "TIERS OR ONE PATH PER LINE"}</span>
          </button>
        ) : (
          <div style={{ marginTop: 12, border: "1px solid rgba(10,10,10,.1)", borderRadius: 14, padding: "14px 16px", display: "flex", alignItems: "center", gap: 12, background: SOFT }}>
            <span style={{ flex: "none", width: 36, height: 36, borderRadius: 9, background: DS.fileTile, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 9, fontWeight: 800, letterSpacing: ".3px", color: DS.surface }} data-testid="picked-file-format">{fileFormat(picked.name)}</span>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div data-testid="picked-file" style={{ fontSize: 14.5, fontWeight: 700, letterSpacing: "-.2px", ...ELLIPSIS }}>{picked.name}</div>
              <div style={{ fontSize: 12.5, color: DS.body2, marginTop: 2 }}>{formatBytes(picked.size)} · ready to check</div>
            </div>
            <button type="button" className="pd-link kb" onClick={onClearPick} style={{ ...LINK_BUTTON, color: DS.body2 }}>Choose a different file</button>
          </div>
        )}
        {error && <FileProblems error={error} />}
      </div>
      <ModalFoot note="Nothing is saved until you review what's inside.">
        <PillButton onClick={onClose}>Cancel</PillButton>
        <button
          type="button"
          className="pd-press kb"
          data-testid="validate-file"
          disabled={!ready}
          onClick={onValidate}
          style={{ height: 44, padding: "0 20px", border: 0, borderRadius: 22, background: picked ? GOLD_BTN : SEG_TRACK, color: picked ? DS.ink : DS.dashed, boxShadow: picked ? GOLD_SHADOW : "none", display: "flex", alignItems: "center", gap: 8, whiteSpace: "nowrap", font: "inherit", fontSize: 14.5, fontWeight: 800, cursor: ready ? "pointer" : "default" }}
        >
          {validating && <Spinner />}
          {validating ? "Checking the file…" : "Check file & preview"}
          {!validating && <DsIcon name="arrowRight" size={14} color={picked ? DS.ink : DS.dashed} />}
        </button>
      </ModalFoot>
    </Modal>
  );
}

function FileProblems({ error }: { error: ProjectFileError }) {
  return (
    <div role="alert" data-testid="project-file-error" style={{ width: "100%", marginTop: 12, padding: "12px 14px", borderRadius: 12, background: DS.redSoft, color: DS.alertInk, fontSize: 13.5, lineHeight: 1.45, boxSizing: "border-box" }}>
      <strong>{error.message}</strong>
      {error.issues.length > 0 && (
        <ul style={{ margin: "6px 0 0", paddingLeft: 18 }}>
          {error.issues.slice(0, 6).map((issue, index) => (
            <li key={index}>
              {issue.row ? `Row ${issue.row}${issue.column ? ` (${issue.column})` : ""}: ` : ""}
              {issue.message}
            </li>
          ))}
          {error.issues.length > 6 && <li>…and {error.issues.length - 6} more.</li>}
        </ul>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// The page
// ---------------------------------------------------------------------------

const clock = (ms: number) => new Date(ms).toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });

function ProviderProblem({ error, source, onRetry, retryAt, onSignIn }: { error: ProjectDetailsError; source: string; onRetry: () => void; retryAt?: number; onSignIn?: () => void }) {
  const disconnected = error === "procore_disconnected" || error === "autodesk_disconnected";
  const limited = error === "rate_limited";
  return (
    <div data-testid="provider-problem" className="pd-enter" style={{ marginTop: 24, border: CARD_LINE, borderRadius: 16, background: DS.surface, padding: "32px 28px", textAlign: "center" }}>
      <div style={{ fontSize: 18, fontWeight: 800, letterSpacing: "-.3px" }}>
        {disconnected ? `Sign in to ${source} to see this` : limited ? `${source} asked us to slow down` : `${source} didn't answer`}
      </div>
      <div style={{ fontSize: 14, lineHeight: 1.5, color: DS.body2, maxWidth: 460, margin: "6px auto 0" }}>
        {disconnected
          ? `This page reads ${source} with your own ${source} sign-in. Sign in once and it works here and in the app.`
          : limited
            ? `We pause requests to stay inside your company's ${source} limit, which your walks' syncing shares.${retryAt ? ` Try again after ${clock(retryAt)}.` : ""}`
            : `We couldn't load this from ${source}. It's usually brief.`}
      </div>
      {disconnected && onSignIn && (
        <button type="button" className="pd-press kb" data-testid="provider-signin" onClick={onSignIn} style={{ height: 40, margin: "16px auto 0", padding: "0 18px", border: 0, borderRadius: 20, background: DS.ink, color: DS.surface, font: "inherit", fontSize: 14, fontWeight: 700, cursor: "pointer" }}>
          Sign in to {source}
        </button>
      )}
      {!disconnected && (
        <button type="button" className="pd-press kb" onClick={onRetry} style={{ ...TOOL_BUTTON, height: 40, margin: "16px auto 0", borderRadius: 20, padding: "0 18px" }}>
          Try again
        </button>
      )}
    </div>
  );
}

function NoProject({ sites, onPick }: { sites: ConsoleSite[]; onPick: (id: string) => void }) {
  return (
    <>
      <div style={{ ...EYEBROW, color: DS.ink }}>All projects</div>
      <h1 style={{ margin: "8px 0 0", fontSize: 30, fontWeight: 800, letterSpacing: "-1px" }}>Project details</h1>
      <p style={{ margin: "8px 0 0", fontSize: 15, lineHeight: 1.55, color: DS.body2, maxWidth: 560 }}>
        Directory and locations live per project. Pick a project to see who's on it and where things are.
      </p>
      <div data-testid="project-details-picker" style={{ marginTop: 22, border: CARD_LINE, borderRadius: 16, background: DS.surface, overflow: "hidden" }}>
        {sites.map((site, index) => (
          <button
            key={site.id}
            type="button"
            className="pd-row kb"
            onClick={() => onPick(site.id)}
            style={{ display: "grid", gridTemplateColumns: "minmax(0,1fr) auto 20px", alignItems: "center", gap: 16, width: "100%", padding: "14px 20px", border: 0, borderTop: index ? ROW_LINE : "none", background: "transparent", cursor: "pointer", textAlign: "left", font: "inherit", color: DS.ink }}
          >
            <span style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0 }}>
              <SourceMark source={site.syncSource} size={16} />
              <span style={{ fontSize: 15, fontWeight: 700, letterSpacing: "-.2px", ...ELLIPSIS }}>{site.name || "Untitled project"}</span>
            </span>
            <span style={{ fontSize: 12.5, fontWeight: 600, color: site.syncSource ? SYNC_INK : DS.body2, whiteSpace: "nowrap" }}>
              {site.syncSource ? `Synced from ${providerName(site.syncSource)}` : "From files"}
            </span>
            <DsIcon name="chevron" size={14} color={DS.dashed} />
          </button>
        ))}
        {sites.length === 0 && <div style={{ padding: "28px 20px", fontSize: 14, color: DS.meta }}>This workspace has no projects yet.</div>}
      </div>
    </>
  );
}

const shortDate = (iso: string) => new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" });

function ago(iso: string): string {
  const minutes = Math.round((Date.now() - new Date(iso).getTime()) / 60_000);
  if (minutes < 1) return "just now";
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.round(minutes / 60);
  return hours < 24 ? `${hours} h ago` : shortDate(iso);
}

export function ProjectDetails(props: ProjectDetailsProps) {
  const { tab, site, sites, details, onReload, search, onNavigate, hrefWithParam } = props;
  const isDir = tab === "directory";
  const [picked, setPicked] = useState<File | null>(null);
  const [validating, setValidating] = useState(false);
  const [problem, setProblem] = useState<ProjectFileError | null>(null);
  const [modal, setModal] = useState<{ mode: "review" | "view"; validation: ProjectFileValidation } | null>(null);
  const [viewing, setViewing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState("");
  const [meta, setMeta] = useState<HalfMeta | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  /** Step 1 of the upload, and whether its "Where to get this file" is open. */
  const [upload, setUpload] = useState<{ helpOpen: boolean } | null>(null);
  const askedSummary = useRef("");

  // A different page or project starts clean: a file picked for the directory
  // must not be offered as this project's location list.
  useEffect(() => {
    setPicked(null);
    setProblem(null);
    setModal(null);
    setUpload(null);
    setMeta(null);
  }, [tab, site?.id]);
  useEffect(() => {
    if (!toast) return;
    const timer = setTimeout(() => setToast(""), 2600);
    return () => clearTimeout(timer);
  }, [toast]);

  const data = details.data && details.data.projectId === site?.id ? details.data : null;

  // The rail's count for this page comes from the summary, which only knows a
  // half once it has been read. When THIS page is what read it, ask the
  // summary again — once — so the rail catches up without a provider call.
  const onMeta = (next: HalfMeta) => {
    setMeta(next);
    const half = data ? (isDir ? data.directory : data.locations) : undefined;
    const marker = `${site?.id}:${tab}`;
    if (data && !half && next.freshness.updatedAt && askedSummary.current !== marker) {
      askedSummary.current = marker;
      onReload();
    }
  };

  const validate = async (file: File) => {
    if (!site) return;
    setValidating(true);
    setProblem(null);
    try {
      const validation = await validateProjectFile(site.id, search, tab, file);
      // Step 2: the review replaces the upload; "Back to upload" returns to it
      // with the same file still chosen.
      setUpload(null);
      setModal({ mode: "review", validation });
    } catch (error) {
      setProblem(error instanceof ProjectFileError ? error : new ProjectFileError(0, (error as Error)?.message || "Could not check this file."));
    } finally {
      setValidating(false);
    }
  };

  const confirm = async () => {
    if (!site || !picked || !modal) return;
    setSaving(true);
    try {
      await saveProjectFile(site.id, search, tab, picked, data?.files?.configVersion ?? 0);
      const count = isDir
        ? plural(modal.validation.directory?.people.length ?? 0, "person", "people")
        : plural(modal.validation.locations?.length ?? 0, "location");
      setModal(null);
      setPicked(null);
      setToast(`${isDir ? "Directory" : "Locations"} saved · ${count} · applies to new walks`);
      clearDetailsCache();
      setReloadKey((n) => n + 1);
      onReload();
    } catch (error) {
      setModal(null);
      setUpload({ helpOpen: false });
      setProblem(error instanceof ProjectFileError ? error : new ProjectFileError(0, (error as Error)?.message || "Could not save this file."));
    } finally {
      setSaving(false);
    }
  };

  /** "View file" shows the WHOLE file in the review modal — one request for
   *  everything, made only when asked (it is our Mongo, not a provider). */
  const viewFile = async () => {
    if (!site || !data?.files) return;
    const file = isDir ? data.files.directory : data.files.locations;
    if (!file) return;
    setViewing(true);
    try {
      if (isDir) {
        const page = await fetchProjectDirectory(site.id, search, { limit: 100 });
        let people = page.people;
        while (people.length < page.matched) {
          const next = await fetchProjectDirectory(site.id, search, { offset: people.length, limit: 100 });
          if (!next.people.length) break;
          people = [...people, ...next.people];
        }
        setModal({ mode: "view", validation: { kind: "directory", filename: file.filename, directory: { people, companies: page.companies.map(({ id, name, trade }) => ({ id, name, ...(trade ? { trade } : {}) })) }, warnings: file.warnings } });
      } else {
        const page = await fetchProjectLocations(site.id, search, { subtree: "root" });
        setModal({ mode: "view", validation: { kind: "locations", filename: file.filename, locations: page.nodes.map(({ id, name, parentId }) => ({ id, name, ...(parentId ? { parentId } : {}) })), warnings: file.warnings } });
      }
    } finally {
      setViewing(false);
    }
  };

  const pick = (file: File) => {
    setProblem(null);
    setPicked(file);
  };

  let body: React.ReactNode;
  let sourceLine: React.ReactNode = null;

  if (!site) {
    body = <NoProject sites={sites} onPick={(id) => onNavigate(hrefWithParam("site", id))} />;
  } else if (!data) {
    body = details.failure ? (
      <FailurePanel failure={details.failure} personalHref={props.personalHref} onGoPersonal={props.onGoPersonal} />
    ) : (
      <LoadingPanel />
    );
  } else {
    const provider = providerName(data.source);
    const file = isDir ? data.files?.directory : data.files?.locations;
    const hasFile = data.source === "files" && !!file;
    const fresh = meta?.freshness;
    const error = data.disconnected ? (data.source === "autodesk" ? "autodesk_disconnected" : "procore_disconnected") : fresh?.error;
    const emptyText = data.source === "files" ? "This file has no rows." : isDir ? `No one is in this project's ${provider} directory yet.` : `This project has no locations in ${provider} yet.`;
    const retry = () => {
      clearDetailsCache();
      setMeta(null);
      setReloadKey((n) => n + 1);
    };

    if (!error && (data.source !== "files" || hasFile)) {
      const counts = meta?.counts;
      sourceLine = (
        <div data-testid="project-details-source" style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16, flexWrap: "wrap", marginTop: 12, padding: "0 2px", fontSize: 13.5, color: DS.body2, minHeight: 20 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
            {data.source !== "files" && <SourceMark source={data.source} />}
            <span style={ELLIPSIS}>
              {data.source === "files" && file
                ? `${file.filename} · v${file.version}${counts ? ` · ${counts}` : ""} · uploaded ${shortDate(file.uploadedAt)}${file.uploadedByName ? ` by ${file.uploadedByName}` : ""}`
                : `Synced from ${provider}${counts ? ` · ${counts}` : ""}${fresh?.updatedAt ? ` · updated ${ago(fresh.updatedAt)}` : ""}`}
            </span>
            {fresh?.stale && fresh.retryAt && (
              <span data-testid="details-stale" title={`${provider} is rate-limiting; showing the last copy.`} style={{ flex: "none", fontSize: 12.5, color: WARN_INK }}>
                · {provider} is busy, refreshing after {clock(fresh.retryAt)}
              </span>
            )}
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 16, flex: "none" }}>
            {data.source !== "files" && data.providerUrls?.[tab] && (
              <a className="pd-link" href={data.providerUrls[tab]} target="_blank" rel="noreferrer" style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: 13, fontWeight: 600, color: DS.ink, textDecoration: "none", whiteSpace: "nowrap" }}>
                Open in {provider}
                <DsIcon name="arrowRight" size={12} style={{ transform: "rotate(-45deg)" }} />
              </a>
            )}
            {hasFile && (
              <>
                <button type="button" className="pd-link kb" disabled={viewing} onClick={() => void viewFile()} style={LINK_BUTTON}>
                  {viewing ? "Opening…" : "View file"}
                </button>
                {data.canManage && (
                  <button type="button" className="pd-link kb" data-testid="replace-file" onClick={() => setUpload({ helpOpen: false })} style={LINK_BUTTON}>
                    Replace file
                  </button>
                )}
              </>
            )}
          </div>
        </div>
      );
    }

    const half = isDir ? (
      <DirectoryBody key={`${site.id}:dir`} siteId={site.id} search={search} reloadKey={reloadKey} onMeta={onMeta} emptyText={emptyText} sourceTag={data.source === "files" ? `${fileFormat(file?.filename)} · V${file?.version ?? 1}` : provider.toUpperCase()} />
    ) : (
      <LocationsBody key={`${site.id}:loc`} siteId={site.id} search={search} reloadKey={reloadKey} onMeta={onMeta} emptyText={emptyText} />
    );

    if (data.source === "files" && !hasFile) {
      body = data.canManage ? (
        <EmptyUpload tab={tab} onOpen={() => setUpload({ helpOpen: true })} onHelp={() => setUpload({ helpOpen: true })} />
      ) : (
        <div data-testid="files-empty-readonly" className="pd-enter" style={{ marginTop: 24, border: CARD_LINE, borderRadius: 16, background: DS.surface, padding: "40px 28px", textAlign: "center" }}>
          <div style={{ fontSize: 18, fontWeight: 800, letterSpacing: "-.3px" }}>{isDir ? "No directory yet" : "No locations yet"}</div>
          <div style={{ fontSize: 14, lineHeight: 1.5, color: DS.body2, marginTop: 6 }}>
            {isDir ? "People will be matched by name only" : "Walks will record locations as free text"} until an admin of this project uploads one.
          </div>
        </div>
      );
    } else if (data.disconnected) {
      body = <ProviderProblem error={error!} source={provider} onRetry={retry} onSignIn={openIntegrations} />;
    } else {
      // The half renders even under an error, so its read can report the
      // error (it draws nothing itself then); the problem panel says why.
      body = (
        <>
          {error && !fresh?.stale && <ProviderProblem error={error} source={provider || "the server"} onRetry={retry} retryAt={fresh?.retryAt} />}
          {half}
        </>
      );
    }
  }

  return (
    <div style={PANE} className="sb" data-pd-scroll>
      <div style={PAGE_BOX} data-testid={`project-details-${tab}`}>
        {site && (
          <div style={{ minWidth: 0 }}>
            <div style={{ ...EYEBROW, color: DS.ink, ...ELLIPSIS }}>Project details{site.name ? ` · ${site.name}` : ""}</div>
            <h1 style={{ margin: "8px 0 0", fontSize: 30, fontWeight: 800, letterSpacing: "-1px" }}>{isDir ? "Directory" : "Locations"}</h1>
          </div>
        )}
        {details.refreshFailure && site && <div style={{ marginTop: 12 }}><StaleBanner failure={details.refreshFailure} /></div>}
        {sourceLine}
        {body}
      </div>
      {modal && modal.validation.directory && (
        <DirectoryReview
          directory={modal.validation.directory}
          filename={modal.validation.filename}
          warnings={modal.validation.warnings}
          review={modal.mode === "review"}
          saving={saving}
          onClose={() => !saving && setModal(null)}
          onBack={() => {
            setModal(null);
            setUpload({ helpOpen: false });
          }}
          onConfirm={() => void confirm()}
        />
      )}
      {modal && modal.validation.locations && (
        <LocationsReview
          locations={modal.validation.locations}
          filename={modal.validation.filename}
          warnings={modal.validation.warnings}
          review={modal.mode === "review"}
          saving={saving}
          onClose={() => !saving && setModal(null)}
          onBack={() => {
            setModal(null);
            setUpload({ helpOpen: false });
          }}
          onConfirm={() => void confirm()}
        />
      )}
      {upload && site && (
        <UploadModal
          tab={tab}
          picked={picked}
          validating={validating}
          error={problem}
          helpOpen={upload.helpOpen}
          onHelpToggle={() => setUpload((u) => ({ helpOpen: !u?.helpOpen }))}
          onPick={pick}
          onClearPick={() => {
            setPicked(null);
            setProblem(null);
          }}
          onValidate={() => picked && void validate(picked)}
          onClose={() => !validating && setUpload(null)}
        />
      )}
      {toast && createPortal(
        <div role="status" data-testid="project-details-toast" className="pd-enter" style={{ position: "fixed", left: "50%", bottom: 128, transform: "translateX(-50%)", zIndex: 50, display: "flex", alignItems: "center", gap: 10, height: 44, padding: "0 18px 0 14px", borderRadius: 22, background: DS.ink, color: DS.surface, boxShadow: "0 14px 34px rgba(10,10,10,.25)", maxWidth: "calc(100vw - 32px)" }}>
          <span style={{ width: 20, height: 20, borderRadius: 10, background: DS.green, display: "flex", alignItems: "center", justifyContent: "center", flex: "none" }}>
            <DsIcon name="check" size={12} color={DS.surface} />
          </span>
          <span style={{ fontSize: 14, fontWeight: 600, ...ELLIPSIS }}>{toast}</span>
        </div>,
        document.body,
      )}
    </div>
  );
}

function ProviderEmpty({ text }: { text: string }) {
  return (
    <div className="pd-enter" style={{ marginTop: 24, border: CARD_LINE, borderRadius: 16, background: DS.surface, padding: "40px 28px", textAlign: "center", fontSize: 14.5, color: DS.body2 }}>
      {text}
    </div>
  );
}
